import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchMarketplaceCategories,
    createMarketplaceCategory,
    updateMarketplaceCategory,
    deleteMarketplaceCategory,
    toggleCategoryStatus
} from "../../../redux/slice/marketplaceSlice";
import CategoryItem from "./CategoryItem";
import DeletePopup from "../../../components/delete-popup";
import CommonButton from "../../../components/commonButton";
import { useToast } from "../../../context/TostContext";
import { usePermission } from "../../../hooks/usePermission";
import CategoryModal from "./CategoryModal";
import { PlusIcon } from "../../../assets/icons";

const MODULE_SLUG = "marketplace_categories";

const MarketplaceCategories = () => {
    const dispatch = useDispatch();
    const { categories, isLoading } = useSelector((state) => state.marketplace);
    const toast = useToast();
    const { hasPermission } = usePermission();

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>Categories
            </h4>
        </div>
    );

    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");
    const canDelete = hasPermission(MODULE_SLUG, "delete");

    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [parentId, setParentId] = useState(null);
    const [categoryName, setCategoryName] = useState("");

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        dispatch(fetchMarketplaceCategories());
    }, [dispatch]);

    const handleAdd = (pId = null) => {
        setIsEdit(false);
        setParentId(pId);
        setCategoryName("");
        setShowModal(true);
    };

    const handleEdit = (category) => {
        setIsEdit(true);
        setSelectedCategory(category);
        setCategoryName(category.name);
        setShowModal(true);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeletePopup(true);
    };

    const handleSubmit = async (name) => {
        if (!name.trim()) {
            toast.error("Category name is required");
            return;
        }

        const data = {
            name: name,
            parent_id: parentId
        };

        try {
            if (isEdit) {
                await dispatch(updateMarketplaceCategory({ id: selectedCategory.id, data: { name } })).unwrap();
            } else {
                await dispatch(createMarketplaceCategory(data)).unwrap();
            }
            setShowModal(false);
            dispatch(fetchMarketplaceCategories());
        } catch (error) {
            // Error is handled by toast in slice
        }
    };

    const confirmDelete = async () => {
        try {
            await dispatch(deleteMarketplaceCategory(deleteId)).unwrap();
            setShowDeletePopup(false);
            dispatch(fetchMarketplaceCategories());
        } catch (error) {
            // Error is handled by toast in slice
        }
    };

    const handleToggle = async (id, is_active) => {
        try {
            await dispatch(toggleCategoryStatus({ id, is_active })).unwrap();
            dispatch(fetchMarketplaceCategories());
        } catch (error) {
            // Error handled by toast
        }
    };

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="bg-white p-4 rounded-2 mb-4 common-shadow">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="m-0"></div>
                        {canCreate && (
                            <CommonButton
                                icon={<PlusIcon size={20} />}
                                title="Add Root Category"
                                onClick={() => handleAdd(null)}
                            />
                        )}
                    </div>

                    <div className="category-tree-wrapper mt-3">
                        {categories && categories.length > 0 ? (
                            categories.map((cat) => (
                                <CategoryItem
                                    key={cat.id}
                                    category={cat}
                                    onAdd={handleAdd}
                                    onEdit={handleEdit}
                                    onDelete={handleDeleteClick}
                                    onToggleStatus={handleToggle}
                                    canCreate={canCreate}
                                    canUpdate={canUpdate}
                                    canDelete={canDelete}
                                />
                            ))
                        ) : (
                            <div className="text-center p-5 text-muted border rounded">
                                {isLoading ? "Loading categories..." : "No categories found. Start by adding a root category."}
                            </div>
                        )}
                    </div>
                </div>

                {/* Add/Edit Modal */}
                <CategoryModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    title={isEdit ? "Edit Category" : "Add Category"}
                    isEdit={isEdit}
                    categoryName={categoryName}
                    onSubmit={handleSubmit}

                />

                {/* Delete Confirmation */}
                <DeletePopup
                    show={showDeletePopup}
                    onHide={() => setShowDeletePopup(false)}
                    onConfirm={confirmDelete}
                    title="Delete Category"
                    description="Are you sure you want to delete this category? This will also affect child categories and listings associated with it."
                />
            </Container>
        </>
    );
};

export default MarketplaceCategories;
